import React, { useCallback, useEffect, useState } from "react"
import { CoreTracking } from "../../../../models/CoreTracking"
import { useDispatch, useSelector } from "react-redux";
import ModalComponent from "../../../misc/ModalComponent"
import { Form, Row, Label, Col } from "reactstrap"
import SelectInput from "../../../misc/SelectInput"
import { EmptyRequestState, RequestState } from "../../../../models/RequestState"
import { GetOneRosterTrackingActivities, UpdateOneRosterTrackingActivity, ClearUpdateOneRosterTrackingActivity } from "../../../../store/OneRoster/OneRosterTrackingOperationAction"
import Toggle from "../../../misc/Toggle";
import { CoreOneRosterMappingTracking, OneRosterTrackingSearchRequest } from "../../../../models/CoreTrackingOperation";
import StatusCode from "../../../../util/StatusCode";
import Search from "../../../misc/Search";
import debounce from 'lodash/debounce';
import * as Helper from '../../../../util/Helper';
import { CoreActivity } from "../../../../models/CoreActivity";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { RootState } from "../../../../store/store";




interface MapOneRosterTrackingActivityProps {
    tracking: CoreTracking
    close: (success: boolean) => void
}


const MapOneRosterTrackingActivity: React.FC<MapOneRosterTrackingActivityProps> = (props) => {
    const dispatch = useDispatch();
    const [search, setSearch] = useState<string>('');
    const [selectedActivity, setSelectedActivity] = useState<CoreOneRosterMappingTracking | undefined>(undefined)
    const [filter, setFilter] = useState<OneRosterTrackingSearchRequest>({ onlyInactive: true, search: '', selfObjectId: props.tracking.flowSyncId });
    const oneRosterActivities = useSelector<RootState, RequestState<CoreOneRosterMappingTracking[]>>(state => state.oneRosterTrackingOperation.getOneRosterActivities)
    const activity = useSelector<RootState, RequestState<CoreActivity>>(state => state.activities.activity);
    const updatedActivity = useSelector<RootState, EmptyRequestState>(state => state.oneRosterTrackingOperation.updateOneRosterActivityTracking);

    const { t } = useTranslation(['common', 'schoolAdministration']);

    useEffect(() => {
        GetOneRosterTrackingActivities(filter)(dispatch);
    }, [dispatch, filter]);

    const close = (success: boolean) => {
        setSelectedActivity(undefined);
        if (updatedActivity.code !== StatusCode.NONE) {
            success = true;
            ClearUpdateOneRosterTrackingActivity()(dispatch);
        }
        props.close(success);
    }

    const submitInput: React.MouseEventHandler<HTMLButtonElement> = () => {
        if (selectedActivity?.trackingId) {
            UpdateOneRosterTrackingActivity(props.tracking.trackingId, selectedActivity.trackingId)(dispatch);
        }
    }

    const debouncedSearch = useCallback(
        debounce((search: string) => {
            setFilter({ ...filter, "search": search });
        }, 300), [filter]);


    const onSearch: React.ChangeEventHandler<HTMLInputElement> = (e) => {
        e.preventDefault();
        const search = e.target.value;
        setSearch(search);
        debouncedSearch(search);
    }

    const updateOneRoster = (value: string | undefined) => {
        if (oneRosterActivities.code === StatusCode.COMPLETE) {
            var selectedActivity = oneRosterActivities.data.find(x => x.trackingId === value)
        }
        setSelectedActivity(selectedActivity);
    }

    const mapOneRosterActivities = () => {
        if (oneRosterActivities.code === StatusCode.COMPLETE) {
            return oneRosterActivities.data.map(x => ({ value: x.trackingId, text: `${x.displayName}, ${x.schoolUnitTitle}, (SIS-ID: ${x.sourceSystemId}) ${x.inactivated ? `, ${t('trackingOperations.mapOneRosterTracking.headers.inactivated', {ns: 'schoolAdministration'})}` : ''}` }))
        }
        return [];
    }

    const count = oneRosterActivities.code === StatusCode.COMPLETE ?
        ((oneRosterActivities.data.length >= 100) ? `(${t('trackingOperations.mapOneRosterTracking.firstHundredAnswers', {ns: 'schoolAdministration'})})`
            : (oneRosterActivities.data.length < 100) ? `(${oneRosterActivities.data.length})` : null)
        : null;

    return <ModalComponent
        size="xl"
        isOpen={true}
        toggleModal={() => close(false)}
        header={t('trackingOperations.mapOneRosterTracking.switch', {ns: 'schoolAdministration'})}
        update={submitInput}
        disabled={!selectedActivity || updatedActivity.code === StatusCode.COMPLETE}
        confirm={t('trackingOperations.mapOneRosterTracking.action.switch', {ns: 'schoolAdministration'})}
        saveStatus={updatedActivity.code}
        successMsg={t('trackingOperations.mapOneRosterTracking.success', {ns: 'schoolAdministration'})}
        cancel={t('action.cancel', {ns: 'common'})}>
        <h5>{t('trackingOperations.mapOneRosterTracking.activity.title', {ns: 'schoolAdministration'})}</h5>
        <Row>
            <Col sm="8">
                <Search
                    value={search}
                    onChange={onSearch}
                    placeholder={t('trackingOperations.mapOneRosterTracking.activity.search', {ns: 'schoolAdministration'})}
                />
            </Col>
            <Col sm="4">
                <Toggle
                    title={t('trackingOperations.mapOneRosterTracking.onlyInactive', {ns: 'schoolAdministration'})}
                    value={filter.onlyInactive}
                    id="onlyInactive"
                    disabled={oneRosterActivities.code !== StatusCode.COMPLETE}
                    update={(value) => setFilter({ ...filter, "onlyInactive": value })}
                />
            </Col>
        </Row>
        <Form>
            <Label>{t('trackingOperations.mapOneRosterTracking.activity.select', {ns: 'schoolAdministration'})} {count}</Label>
            <SelectInput
                name="string"
                defaultText={t('trackingOperations.mapOneRosterTracking.activity.select', { ns: 'schoolAdministration' })}
                selected={selectedActivity?.trackingId}
                disabled={oneRosterActivities.code !== StatusCode.COMPLETE}
                onChange={(name, value) => updateOneRoster(value)}
                items={mapOneRosterActivities()}
            />

            {selectedActivity?.trackingId && <>
                <hr />
                <h5>{t('trackingOperations.mapOneRosterTracking.switch', {ns: 'schoolAdministration'})}</h5>
                <p>{t('trackingOperations.mapOneRosterTracking.activity.text', { ns: 'schoolAdministration' })}</p>
                <Row className="switch-oneRosterTrackings-row">
                    <Col sm="5" className="switch-oneRosterTrackings">
                        {activity.code === StatusCode.COMPLETE && <>
                            <p><b>{t('trackingOperations.mapOneRosterTracking.headers.activity', { ns: 'schoolAdministration' })}:</b> <Link to={`/activity/${activity.data.id}`} target="_blank">{activity.data.displayName}</Link></p>
                            <p><b>{t('trackingOperations.mapOneRosterTracking.headers.class', { ns: 'schoolAdministration' })}:</b> <Link to={`/oneroster/class/${props.tracking.sourceSystemId}`} target="_blank">{activity.data.displayName}</Link></p>
                            <p><b>{t('trackingOperations.mapOneRosterTracking.headers.school', { ns: 'schoolAdministration' })}:</b> {activity.data.schoolUnit?.title}</p>
                            <p><b>{t('trackingOperations.mapOneRosterTracking.headers.sisId', { ns: 'schoolAdministration' })}: </b>{props.tracking.sourceSystemId}</p>
                        </>}
                    </Col>
                    <Col sm="2" className="icon-column">
                        <FontAwesomeIcon icon="shuffle" />
                    </Col>
                    <Col sm="5" className="switch-oneRosterTrackings">
                        <p><b>{t('trackingOperations.mapOneRosterTracking.headers.activity', { ns: 'schoolAdministration' })}:</b> <Link to={`/activity/${selectedActivity.flowSyncId}`} target="_blank">{selectedActivity.displayName}</Link></p>
                        <p><b>{t('trackingOperations.mapOneRosterTracking.headers.class', { ns: 'schoolAdministration' })}:</b> <Link to={`/oneroster/class/${selectedActivity.sourceSystemId}`} target="_blank">{selectedActivity.displayName}</Link></p>
                        <p><b>{t('trackingOperations.mapOneRosterTracking.headers.school', { ns: 'schoolAdministration' })}:</b> {selectedActivity.schoolUnitTitle}</p>
                        <p><b>{t('trackingOperations.mapOneRosterTracking.headers.sisId', { ns: 'schoolAdministration' })}: </b>{selectedActivity.sourceSystemId}</p>
                        <p><b>{t('trackingOperations.mapOneRosterTracking.headers.inactivated', { ns: 'schoolAdministration' })}:</b> {Helper.formatDate(selectedActivity.inactivated)} </p>
                    </Col>
                </Row>
            </>
            }
        </Form>
    </ModalComponent>
}

export default MapOneRosterTrackingActivity;
